import type { Dayjs, OpUnitType } from "dayjs";
import dayjs from "dayjs";
import _ from "lodash";

import {
    apiFormat,
    customRangeFormat,
    customRangeWithDayFormat,
    shortMonthDayAndYear,
} from "@similarweb/pro-logic-components";

import { swSettings } from "app/common/services/swSettings";
import { CAMPAIGNS_ALL_TIME_ID } from "app/components/campaigns/queryBar-report/reports-pages-filters/durationPicker/CampaignDurationPicker.consts";
import { getAllTimeDateRangeForCampaign } from "app/components/campaigns/queryBar-report/reports-pages-filters/durationPicker/CampaignDurationPickerUtils";
import { i18nFilter } from "app/filters/i18nFilter";
import type { Translate } from "app/filters/i18nFilter";
import { getDiffCustomRangeParam } from "app/helpers/duration/customRange";
import type { DatesRange } from "app/pages/sales-intelligence/sub-modules/account-overview/shared/types/common";
import type { GranularityNames } from "app/UtilitiesAndConstants/Constants/Moment";
import { granularityNameToMomentUnit } from "app/UtilitiesAndConstants/Constants/Moment";
import type { ValueOf } from "pro-features/types";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import type { IPeriodOverPeriodCompareType } from "./periodOverPeriodTypes";

const ISO_DATE_FORMAT = "YYYY-MM-DD";

type durationLatestParams = "28d" | "l";
export type dateForTooltip = { from: string; to: string };
export interface IDurationObject<T> {
    from: T;
    to: T;
    isWindow?: boolean;
    isDaily?: boolean;
    compareTo?: T;
    compareFrom?: T;
    isCustom?: boolean;
    latest?: durationLatestParams;
}
interface IForAPIData extends IDurationObject<string> {
    iso: Pick<IDurationObject<string>, "from" | "to" | "compareFrom" | "compareTo">;
}

export interface IDurationData {
    forAPI: IForAPIData;
    raw: IDurationObject<Dayjs>;
    forTitle: string | string[];
    forWidget: string | string[];
    forTooltip: dateForTooltip | dateForTooltip[] | string;
}

export { apiFormat, customRangeFormat };

const uiFormat = "MMM, YYYY";
const uiFormatDaily = "MMM D, YYYY";
const widgetFormat = "MMM YYYY";
const widgetFormatDaily = "MMM D, YYYY";
const windowFormat = "MMM DD";
const diffUnitSymbol = {
    days: "d",
    weeks: "w",
    months: "m",
    years: "y",
};
const customDateRangeRegexp = /^[0-9]{4}\.[0-9]{2}(\.[0-9]{2})?\-[0-9]{4}\.[0-9]{2}(\.[0-9]{2})?$/;
const relativeDateRangeRegexp = /^[0-9]+[dwmy]{1}$/;
const supportedRelativeDays = [7, 28];
const windowDays = [28];
export const BasicDurations = {
    LAST_TWENTY_EIGHT_DAYS: "28d",
    LAST_MONTH: "1m",
    LAST_TOW_MONTHS: "2m",
    LAST_THREE_MONTHS: "3m",
    LAST_SIX_MONTHS: "6m",
    LAST_TWELVE_MONTHS: "12m",
    LAST_THIRTEEN_MONTHS: "13m",
    LAST_TWENTY_FIVE_MONTHS: "25m",
    LAST_THIRTY_SEVEN_MONTHS: "37m",
} as const;

export type BasicDurationValues = ValueOf<typeof BasicDurations>;

const LESS_THAN_OR_EQUAL_TO_ONE_MONTHS_DURATIONS = [BasicDurations.LAST_TWENTY_EIGHT_DAYS, BasicDurations.LAST_MONTH];
const LESS_THAN_OR_EQUAL_TO_THREE_MONTHS_DURATIONS = [
    ...LESS_THAN_OR_EQUAL_TO_ONE_MONTHS_DURATIONS,
    BasicDurations.LAST_THREE_MONTHS,
];

const LESS_THAN_OR_EQUAL_TO_SIX_MONTHS_DURATIONS = [
    ...LESS_THAN_OR_EQUAL_TO_THREE_MONTHS_DURATIONS,
    BasicDurations.LAST_SIX_MONTHS,
];

export class DurationService {
    private i18n: Translate;

    constructor(i18n = i18nFilter()) {
        this.i18n = i18n;
    }

    public isMonthToDateSupportedForDuration(durationParam: string): boolean {
        // MTD is not supported in periods that are shorter than one month
        if (durationParam === "28d") {
            return false;
        }

        // In any other case - MTD should be supported only in case the selected
        // duration end month matches the latest supported month on the swSettings.
        const selectedDuration = this.getDurationData(durationParam);
        const latestSupportedMonth = swSettings.current.endDate.month();
        const selectedDurationEndMonth = selectedDuration.raw.to.month();
        return selectedDurationEndMonth === latestSupportedMonth;
    }

    public getFormattedDateOutOfDurationParam(durationParam: string, dateFormat: string) {
        return dayjs(this.getDurationData(durationParam).raw.to).format(dateFormat);
    }

    public getDurationData(
        durationParam: string,
        comparedDuration?: string,
        componentId?: string,
        usePermissions = true,
    ): IDurationData {
        const component = componentId ? swSettings.components[componentId] : swSettings.current;
        const mainDurationObject: IDurationData = {} as IDurationData;

        mainDurationObject.raw = this._createDurationObject(durationParam, component);

        const texts = this.createTextRepresentation(mainDurationObject.raw);
        mainDurationObject.forTitle = texts.uiString;
        mainDurationObject.forWidget = texts.widgetString;
        mainDurationObject.forTooltip = texts.forTooltip;

        if (comparedDuration) {
            const calculatedOffset = this._calculateOffset(
                mainDurationObject.raw,
                comparedDuration,
                component,
                usePermissions,
            );
            if (calculatedOffset !== null) {
                mainDurationObject.raw.compareFrom = calculatedOffset.from;
                mainDurationObject.raw.compareTo = calculatedOffset.to;

                const comparedTexts = this.createTextRepresentation(calculatedOffset);
                mainDurationObject.forTitle = [mainDurationObject.forTitle, comparedTexts.uiString];
                mainDurationObject.forWidget = [mainDurationObject.forWidget, comparedTexts.widgetString];
                mainDurationObject.forTooltip = [mainDurationObject.forTooltip, comparedTexts.forTooltip];
            }
        }

        mainDurationObject.forAPI = this._createApiParams(mainDurationObject.raw);

        return mainDurationObject;
    }

    public getDiffCustomRangeParam(from: string | Dayjs, to: string | Dayjs) {
        const fromDayjs = dayjs.isDayjs(from) ? from : dayjs.utc(from, apiFormat);
        const toDayjs = dayjs.isDayjs(to) ? to : dayjs.utc(to, apiFormat);
        return getDiffCustomRangeParam(fromDayjs, toDayjs);
    }

    /**
     * @param availablePresets - sorted array of available presets (e.g. ["1m", "46d", "6m", "12m"])
     * @returns
     */
    public getClosestPreset(from: string | Dayjs, to: string | Dayjs, availablePresets: string[]) {
        if (!Array.isArray(availablePresets) || availablePresets.length === 0) {
            return undefined;
        }
        if (availablePresets.length === 1) {
            return availablePresets[0];
        }
        const numberOfDays = this.diffByUnit(from, to, "days");

        const calculatedPresetNumberOfDays: number[] = [];
        for (let i = 0; i < availablePresets.length - 1; i++) {
            const getPresetNumberOfDaysByIndex = (index: number) => {
                if (calculatedPresetNumberOfDays[index]) {
                    return calculatedPresetNumberOfDays[index];
                }

                const preset = availablePresets[index];
                const presetParsed = this.parsedDurationParam(preset);
                const numberOfDaysForPreset = dayjs
                    .duration(presetParsed.count, presetParsed.unit.toLowerCase() === "d" ? "day" : "month")
                    .as("days");
                calculatedPresetNumberOfDays[index] = numberOfDaysForPreset;
                return numberOfDaysForPreset;
            };

            // calculate which is closest to numberOfDays
            const diffToCurrentPreset = Math.abs(getPresetNumberOfDaysByIndex(i) - numberOfDays);
            const diffToNextPreset = Math.abs(getPresetNumberOfDaysByIndex(i + 1) - numberOfDays);
            if (diffToCurrentPreset < diffToNextPreset) {
                return availablePresets[i];
            } else if (i === availablePresets.length - 2) {
                return availablePresets[i + 1];
            }
        }
    }

    public getDiffSymbol(from: Dayjs, to: Dayjs, diffUnit: keyof typeof diffUnitSymbol = "months") {
        const diff = this.diffByUnit(from, to, diffUnit);
        return _.isNumber(diff)
            ? diff + 1 + diffUnitSymbol[diffUnit]
            : `${from.format(customRangeFormat)}-${to.format(customRangeFormat)}`;
    }

    public getMonthsFromApiDuration(from: string | Dayjs, to: string | Dayjs, isWindow?: boolean) {
        if (isWindow) {
            return 0;
        } else {
            return this.diffByUnit(from, to, "months") + 1;
        }
    }

    public getDurationApiFor(from: Dayjs, to: Dayjs, isWindow?: boolean) {
        const apiParams: any = {
            isWindow: typeof isWindow !== "undefined" ? isWindow : false,
            from: from.format(apiFormat),
            to: dayjs.isDayjs(to) ? to.format(apiFormat) : from.format(apiFormat),
        };
        if (isWindow) {
            apiParams.latest = "28d";
        }
        return apiParams;
    }

    public createRange(from: Dayjs, to: Dayjs, unit: OpUnitType, format = "YYYY-MM-DD") {
        let tempDate = from.clone();
        const result: string[] = [];
        while (tempDate.isBetween(from, to, unit, "[]")) {
            result.push(tempDate.format(format));
            tempDate = tempDate.add(1, unit);
        }

        return result;
    }

    // @ts-ignore FIXME: strict migration — function drops off end when durationParam matches neither custom nor relative regex; legacy callers depend on undefined-fallthrough
    private _createDurationObject(durationParam: string, component: any): IDurationObject<Dayjs> {
        // empty duration
        if (_.isEmpty(durationParam)) {
            const now = dayjs();
            return {
                from: now.clone(),
                to: now.clone(),
                isWindow: false,
            };
        }

        // custom range
        if (customDateRangeRegexp.test(durationParam)) {
            const [from, to] = durationParam.split("-");
            const [fromYear, fromMonth, fromDay] = from.split(".");
            const [toYear, toMonth, toDay] = to.split(".");
            // daily
            if (fromDay && toDay) {
                return {
                    from: swSettings.momentFromString(from),
                    to: swSettings.momentFromString(to),
                    isCustom: true,
                    isWindow: false,
                    isDaily: true,
                };
            } else {
                return {
                    from: this._startOfMonth(swSettings.momentFromString(from)),
                    to: this._endOfMonth(swSettings.momentFromString(to)),
                    isCustom: true,
                    isWindow: false,
                    isDaily: false,
                };
            }
        }

        if (relativeDateRangeRegexp.test(durationParam)) {
            const { unit, count } = this.parsedDurationParam(durationParam);
            const isDaily = unit === "D" && supportedRelativeDays.includes(count);
            const isWeekly = unit === "W";
            const dateFallback = dayjs();
            const to =
                isDaily || isWeekly
                    ? (component.windowEndDate ?? component.endDate ?? dateFallback)
                    : this._endOfMonth((component.endDate ?? dateFallback).clone());

            if (isDaily) {
                const isWindow = windowDays.includes(count);
                const from = isWindow
                    ? (component.windowStartDate ?? to.subtract(count - 1, unit.toLowerCase()))
                    : to.clone().subtract(count - 1, unit.toLowerCase());

                return {
                    to,
                    from,
                    isWindow,
                    // in case of '28d', turn off this flag
                    isDaily: !isWindow,
                };
            } else if (isWeekly) {
                const endIsoWeek = to.isoWeek() - 1;
                const startIsoWeek = endIsoWeek - count + 1;
                return {
                    from: dayjs.utc().isoWeek(startIsoWeek).startOf("isoWeek"),
                    to: dayjs.utc().isoWeek(endIsoWeek).endOf("isoWeek"),
                    isDaily: true,
                    isWindow: false,
                };
            }
            // last N months|years
            else {
                return {
                    to,
                    from: this._startOfMonth(to.clone().subtract(count - 1, unit)),
                    isWindow: false,
                };
            }
        }
    }

    public getDurationDataForCampaigns(duration: string, campaignDataStart: Dayjs, maxDate?: string): IDurationData {
        let durationObject: DatesRange;
        if (duration === CAMPAIGNS_ALL_TIME_ID) {
            durationObject = getAllTimeDateRangeForCampaign(campaignDataStart, maxDate);
        } else {
            durationObject = this.getDurationObjectForCampaigns(duration, maxDate);
        }
        const forAPI = {
            iso: {
                from: durationObject?.from?.format(ISO_DATE_FORMAT),
                to: durationObject?.to?.format(ISO_DATE_FORMAT),
            },
            from: durationObject?.from?.format(apiFormat),
            to: durationObject?.to?.format(apiFormat),
        };
        const datesAsString = `${durationObject?.from?.format(shortMonthDayAndYear)}-${durationObject?.to?.format(
            shortMonthDayAndYear,
        )}`;
        return {
            forAPI,
            raw: durationObject,
            forTitle: datesAsString,
            forWidget: datesAsString,
            forTooltip: datesAsString,
        };
    }

    public getDurationObjectForCampaigns(
        duration: string,
        lastAvailableDate: string = swSettings.components.RankTracker?.resources?.LastAvailableRankTrackerEtlDate,
    ): DatesRange {
        // Custom dotted ranges only; relative presets like "-1d" must not take this path.
        if (customDateRangeRegexp.test(duration)) {
            // which means it's a custom range
            const [from, to] = duration.split("-");
            return {
                from: dayjs(from, customRangeWithDayFormat),
                to: dayjs(to, customRangeWithDayFormat),
            };
        } else {
            const { unit, count } = this.parsedDurationParam(duration);
            const from =
                count === 0
                    ? dayjs(lastAvailableDate)
                    : dayjs(lastAvailableDate).subtract(count, unit !== "D" ? unit : unit.toLowerCase());
            return {
                from,
                to: dayjs(lastAvailableDate),
            };
        }
    }

    public getArrayOfAllSelectedDates = (durationObject: IDurationObject<Dayjs>, dateFormat: string): string[] => {
        const startDate = durationObject.from;
        const endDate = durationObject.to;

        let date = startDate;
        const dates = [];

        while (date.isBefore(endDate) || date.isSame(endDate)) {
            dates.push(date.format(dateFormat));
            date = date.add(1, "day");
        }
        return dates;
    };

    public parsedDurationParam(durationParam: string): any {
        const unit = durationParam.replace(/\d+/g, "").toUpperCase();
        // eslint:disable-next-line:radix
        const count = parseInt(durationParam.replace(/[a-zA-Z]+/g, ""));
        return { unit, count };
    }

    public createTextRepresentation(durationObject: IDurationObject<Dayjs>) {
        const result: any = {};

        if (durationObject.isDaily) {
            if (durationObject.isCustom) {
                result.uiString = this.i18n("global.from", {
                    param1: durationObject.from.format(uiFormatDaily),
                    param2: durationObject.to.format(uiFormatDaily),
                });
                result.widgetString =
                    durationObject.from.format(widgetFormatDaily) + " - " + durationObject.to.format(widgetFormatDaily);
            } else {
                const count = durationObject.to.diff(durationObject.from, "d") + 1;
                result.uiString = this.i18n("global.from_window_generic", {
                    date: durationObject.to.format(windowFormat),
                    count: count.toString(),
                });
                result.widgetString = this.i18n("global.from_window_generic", {
                    date: durationObject.to.format(windowFormat),
                    count: count.toString(),
                });
            }
        } else if (durationObject.isWindow) {
            const count = durationObject.to.diff(durationObject.from, "d") + 1;
            result.uiString = this.i18n("global.from_window_generic", {
                date: durationObject.to.format(windowFormat),
                count: count.toString(),
            });
            result.widgetString = this.i18n("global.from_window_generic", {
                date: durationObject.to.format(windowFormat),
                count: count.toString(),
            });
        }

        // same month and year
        else if (
            durationObject.from.month() === durationObject.to.month() &&
            durationObject.from.year() === durationObject.to.year()
        ) {
            result.uiString = this.i18n("global.for", {
                param1: durationObject.from.format(uiFormat),
            });
            result.widgetString = durationObject.from.format(widgetFormat);
        } else {
            result.uiString = this.i18n("global.from", {
                param1: durationObject.from.format(uiFormat),
                param2: durationObject.to.format(uiFormat),
            });
            result.widgetString =
                durationObject.from.format(widgetFormat) + " - " + durationObject.to.format(widgetFormat);
        }

        if (durationObject.isDaily) {
            const clonedTo = durationObject.to.clone();
            const prevWeek = clonedTo.clone().subtract(7, "days");
            result.forTooltip = {
                from: `${prevWeek.startOf("isoWeek").format("MMM D")}-${prevWeek.endOf("isoWeek").format("MMM D")}`,
                to: `${clonedTo.startOf("isoWeek").format("MMM D")}-${clonedTo.endOf("isoWeek").format("MMM D")}`,
            };
        } else {
            // always two last months
            result.forTooltip = {
                from: durationObject.to.clone().subtract(1, "months").format(encodeURIComponent(uiFormat)),
                to: durationObject.to.format(encodeURIComponent(uiFormat)),
            };
        }
        return result;
    }

    private _createApiParams(durationObject: IDurationObject<Dayjs>) {
        const apiParams: IForAPIData = {
            iso: {
                to: durationObject.to.format(ISO_DATE_FORMAT),
                from: durationObject.from.format(ISO_DATE_FORMAT),
                compareTo: durationObject.compareTo && durationObject.compareTo.format(ISO_DATE_FORMAT),
                compareFrom: durationObject.compareFrom && durationObject.compareFrom.format(ISO_DATE_FORMAT),
            },
            to: durationObject.to.format(apiFormat),
            from: durationObject.from.format(apiFormat),
            compareTo: durationObject.compareTo && durationObject.compareTo.format(apiFormat),
            compareFrom: durationObject.compareFrom && durationObject.compareFrom.format(apiFormat),
            isWindow: durationObject.isWindow,
            isDaily: durationObject.isDaily,
        };
        if (durationObject.isWindow) {
            apiParams.latest = "28d";
        }

        return apiParams;
    }

    private _endOfMonth(momentObj: Dayjs) {
        return momentObj.endOf("month").startOf("day");
    }

    private _startOfMonth(momentObj: Dayjs) {
        return momentObj.startOf("month").startOf("day");
    }

    private _calculateOffset(
        durationObject: IDurationObject<Dayjs>,
        durationParam: string,
        component: any,
        usePermissions: boolean,
    ): IDurationObject<Dayjs> | null {
        // TODO: add support of custom offset
        let calculated: IDurationObject<Dayjs> | null = {} as IDurationObject<Dayjs>;

        const { unit, count } = this.parsedDurationParam(durationParam);
        const minStartDate: Dayjs = component.startDate;
        calculated.from = this._startOfMonth(durationObject.from.clone().subtract(count, unit));
        calculated.to = this._endOfMonth(durationObject.to.clone().subtract(count, unit));

        if (usePermissions) {
            // check that the compared duration is permitted
            if (calculated.from.isBefore(minStartDate)) {
                // if the compared range is partially allowed, return the valid part only
                if (calculated.to.isAfter(minStartDate)) {
                    calculated.from = minStartDate;
                }
                // special case when the user select compare which is not valid at all
                else {
                    // @ts-ignore FIXME: strict migration — legacy null-sentinel pattern; runtime caller at line 142 guards with !== null
                    calculated = null;
                }
            }
        }

        return calculated;
    }

    public diffByUnit(from: string | Dayjs, to: string | Dayjs, unit: OpUnitType) {
        const fromMoment = dayjs.isDayjs(from) ? from : dayjs.utc(from, apiFormat);
        const toMoment = dayjs.isDayjs(to) ? to : dayjs.utc(to, apiFormat);
        return toMoment.diff(fromMoment, unit);
    }

    public getMonthsFromDuration(duration: string) {
        const { from, to, isWindow } = this.getDurationData(duration).raw;
        return this.getMonthsFromApiDuration(from, to, isWindow);
    }

    public getMonthsBetweenFromAndTo(duration: string, unit: GranularityNames) {
        const { from, to } = this.getDurationData(duration).forAPI;
        return this.diffByUnit(from, to, granularityNameToMomentUnit[unit]);
    }

    // the dayjs library calculates the difference and truncate the result to zero decimal places
    // therefore for example 2021|03|01 - 2021|04|30 is a 1 month diff.
    public isLessThanOrEqualToOneMonths(durationParam: string) {
        const { from, to } = this.getDurationData(durationParam).forAPI;
        const monthsBetweenFromAndTo = this.diffByUnit(from, to, granularityNameToMomentUnit.Monthly);
        return monthsBetweenFromAndTo < 1;
    }

    public isGreaterThanThreeMonths(durationParam: (typeof LESS_THAN_OR_EQUAL_TO_THREE_MONTHS_DURATIONS)[number]) {
        return !LESS_THAN_OR_EQUAL_TO_THREE_MONTHS_DURATIONS.includes(durationParam);
    }

    public isGreaterThanSixMonths(durationParam: (typeof LESS_THAN_OR_EQUAL_TO_SIX_MONTHS_DURATIONS)[number]) {
        return !LESS_THAN_OR_EQUAL_TO_SIX_MONTHS_DURATIONS.includes(durationParam);
    }

    public isPeriodOverPeriod() {
        const { comparedDuration } = proNavigator.getParams();
        return ![null, undefined, ""].includes(comparedDuration);
    }

    clearPeriodOverPeriod() {
        proNavigator.applyUpdateParams({ comparedDuration: undefined });
    }

    setPeriodOverPeriod(periodOverPeriodCompareType: IPeriodOverPeriodCompareType) {
        const { duration } = proNavigator.getParams();
        const { from, to } = this.getDurationData(duration).forAPI;
        // @ts-ignore FIXME: getDiffSymbol expects Dayjs but forAPI.from/to are strings — callers should use raw (Dayjs) dates instead
        const periodOverPeriodComparedDuration = this.getDiffSymbol(from, to);

        const comparedDuration =
            periodOverPeriodCompareType === "previous year"
                ? BasicDurations.LAST_TWELVE_MONTHS
                : periodOverPeriodComparedDuration;
        proNavigator.applyUpdateParams({ comparedDuration });
    }

    getPeriodOverPeriodCompareType(): IPeriodOverPeriodCompareType | null {
        const { comparedDuration } = proNavigator.getParams();
        if (!comparedDuration) {
            return null;
        }
        return comparedDuration === BasicDurations.LAST_TWELVE_MONTHS ? "previous year" : "previous period";
    }
}
export const DurationServiceFactory = (i18n?: Translate) => {
    return new DurationService(i18n);
};

export default new DurationService();
